import React from 'react';
import ChartUpIcon from 'images/dashboard/chart-up.svg';
import ChartDownIcon from 'images/dashboard/chart-down.svg';
import { getValueByPath, formatValue } from './metaUtils';

// ChartSideMeta renders a vertical stack of meta items on the left of charts.
// Config shape:
// sideMeta: {
//   items: [
//     {
//       label: { path?, default?, format?: 'string' },
//       value: { path?, default?, format?: 'number'|'currency'|'percent', prefix?, suffix?, maximumFractionDigits? },
//       delta?: { path? | value? | default?, isPercent?: boolean, invert?: boolean, visible?: boolean }
//       visible?: boolean
//     }
//   ]
// }

export default function ChartSideMeta({ sideMeta, dataContext }) {
    if (!sideMeta || !Array.isArray(sideMeta.items) || sideMeta.items.length === 0) {
        return null;
    }

    // Internal typography variants (extensible)
    const VALUE_TYPOGRAPHY = {
        md: 'text-base font-semibold',
        lg: 'text-2xl font-semibold',
    };
    const LABEL_TYPOGRAPHY = {
        xs: 'text-xs font-medium',
        sm: 'text-sm font-medium',
        md: 'text-base font-medium',
        lg: 'text-lg font-medium',
    };

    const resolveField = (cfg, defaultFormat) => {
        const normalized = (typeof cfg === 'string' || typeof cfg === 'number') ? { default: cfg } : (cfg || {});
        const path = typeof normalized.path === 'string' ? normalized.path : undefined;
        const fallback = normalized.hasOwnProperty('default') ? normalized.default
            : (normalized.hasOwnProperty('value') ? normalized.value
                : (normalized.hasOwnProperty('text') ? normalized.text : undefined));
        const raw = path ? getValueByPath(dataContext, path) : undefined;
        const effective = (raw !== undefined && raw !== null) ? raw : fallback;
        const format = normalized.format || defaultFormat;
        const prefix = normalized.prefix || '';
        const suffix = normalized.suffix || '';
        const maximumFractionDigits = normalized.maximumFractionDigits ?? 2;
        const display = formatValue(effective, format, { prefix, suffix, maximumFractionDigits });
        const hasValue = effective !== undefined && effective !== null && String(display).length > 0;
        const isVisible = normalized.visible !== false; // default true
        return { display, hasValue, isVisible };
    };

    return (
        <div className={`flex flex-col justify-end gap-3`}>
            {sideMeta.items.filter(it => it?.visible !== false).map((item, idx) => {
                const { display: label, hasValue: hasLabel, isVisible: showLabel } = resolveField(item.label, 'string');
                const { display: value, hasValue: hasValueDisplay, isVisible: showValue } = resolveField(item.value, 'number');

                // Determine value typography classes based on internal variants
                const sizeKey = typeof item?.value?.size === 'string' ? item.value.size : 'lg';
                const sizeClasses = VALUE_TYPOGRAPHY[sizeKey] || VALUE_TYPOGRAPHY.lg;
                const colorClass = typeof item?.value?.color === 'string' ? item.value.color : '';
                const valueClasses = `mb-0 ${sizeClasses} ${colorClass}`.trim();

                // Determine label typography classes based on internal variants
                const labelSizeKey = typeof item?.label?.size === 'string' ? item.label.size : 'md';
                const labelSizeClasses = LABEL_TYPOGRAPHY[labelSizeKey] || LABEL_TYPOGRAPHY.md;
                const labelColorClass = typeof item?.label?.color === 'string' ? item.label.color : '';
                const labelClasses = `${labelSizeClasses} ${labelColorClass} truncate`.trim();

                const deltaCfg = item.delta || {};
                const deltaRaw = deltaCfg?.path ? getValueByPath(dataContext, deltaCfg.path) : (deltaCfg?.value ?? deltaCfg?.default);
                const deltaNumber = typeof deltaRaw === 'number' ? deltaRaw : Number(deltaRaw);
                const deltaIsNumber = !Number.isNaN(deltaNumber) && deltaRaw != null;
                const isPositive = deltaCfg?.invert ? deltaNumber <= 0 : deltaNumber >= 0;
                const showDelta = deltaCfg?.visible !== false && deltaIsNumber;

                return (
                    <div key={item.id || idx} className={`flex flex-col flex-grow justify-center gap-1`}>
                        {showLabel && hasLabel ? (
                            <p className={labelClasses}>{label}</p>
                        ) : null}
                        {showValue && hasValueDisplay ? (
                            <h3 className={valueClasses}>{value}</h3>
                        ) : null}
                        {deltaCfg?.visible ? (
                            <div className={`w-max flex flex-nowrap gap-1 items-center rounded-md py-[6px] px-2 ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
                                <span className={`text-base font-medium ${isPositive ? 'text-green-600' : 'text-red-600'} text-nowrap`}>
                                    {isPositive ? '+ ' : '- '}{Math.abs(deltaNumber)}{deltaCfg?.isPercent !== false ? '%' : ''}
                                </span>
                                <img src={isPositive ? ChartUpIcon : ChartDownIcon} alt="" className="h-3 w-3 flex-shrink-0 block" />
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}


